import { useAppStore } from "@hooks/useAppStore";
import { getSubmittedRawInputValue } from "@memorang/helpers/user-submitted-answer";
import type {
	BaseItemSchema,
	OriginalItemTypes,
	ResponsePrepTime,
	WordLimit,
} from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import SummarizeSpokenText from "@memorang/ui/components/StudySession/SessionContent/item-types/SummarizeSpokenText";
import SummarizeWrittenText from "@memorang/ui/components/StudySession/SessionContent/item-types/SummarizeWrittenText";
import WriteEmail from "@memorang/ui/components/StudySession/SessionContent/item-types/WriteEmail";
import WriteEssay from "@memorang/ui/components/StudySession/SessionContent/item-types/WriteEssay";
import WriteFromDictation from "@memorang/ui/components/StudySession/SessionContent/item-types/WriteFromDictation";
import { memo, useCallback } from "react";
import FormativeTimeOverrunContainer from "../../../containers/FormativeTimeOverrunContainer";
import {
	getAllowAudioReplay,
	getSummativeResponseConfig,
} from "../../../helpers/response-timing";
import useAIFeedbackStore from "../../../hooks/useAIFeedbackStore";
import { useMarkResponseChangedIfGated } from "../../../hooks/useEnqueueAssessmentOnLeave";
import useItemAnsweredState from "../../../hooks/useItemAnsweredState";
import useResponseControlsStore from "../../../hooks/useResponseControlsStore";
import DesktopFeedbackPanel from "./DesktopFeedbackPanel";

type WritingItemInputProps = {
	itemType?: OriginalItemTypes;
	audioUri: string;
	stimulusText: string;
	stem: string;
	instruction: string;
	value?: string;
	onChangeText: (value: string) => void;
	disabled?: boolean;
	wordLimit?: WordLimit;
	prepTime?: ResponsePrepTime;
	timeLimit?: number;
	initialElapsedSeconds?: number;
	allowAudioReplay?: boolean;
	onResponseComplete: () => void;
	onAudioEnded: () => void;
};

const WritingItemInput = memo(function WritingItemInput({
	itemType,
	audioUri,
	stimulusText,
	stem,
	instruction,
	value,
	onChangeText,
	disabled,
	wordLimit,
	prepTime,
	timeLimit,
	initialElapsedSeconds,
	allowAudioReplay,
	onResponseComplete,
	onAudioEnded,
}: WritingItemInputProps) {
	if (itemType === "SSTL" || itemType === "SSTL_CORE") {
		return (
			<SummarizeSpokenText
				audioUri={audioUri}
				instruction={instruction}
				value={value}
				onChangeText={onChangeText}
				disabled={disabled}
				wordLimit={wordLimit}
				prepTime={prepTime}
				timeLimit={timeLimit}
				initialElapsedSeconds={initialElapsedSeconds}
				allowAudioReplay={allowAudioReplay}
				onResponseComplete={onResponseComplete}
				onAudioEnded={onAudioEnded}
			/>
		);
	}

	if (itemType === "SWTR" || itemType === "SWTR_CORE") {
		return (
			<SummarizeWrittenText
				stimulusText={stimulusText}
				instruction={instruction}
				value={value}
				onChangeText={onChangeText}
				disabled={disabled}
				wordLimit={wordLimit}
				timeLimit={timeLimit}
				initialElapsedSeconds={initialElapsedSeconds}
				onResponseComplete={onResponseComplete}
			/>
		);
	}

	if (itemType === "WESW") {
		return (
			<WriteEssay
				stem={stimulusText}
				instruction={instruction}
				value={value}
				onChangeText={onChangeText}
				disabled={disabled}
				wordLimit={wordLimit}
				timeLimit={timeLimit}
				initialElapsedSeconds={initialElapsedSeconds}
				onResponseComplete={onResponseComplete}
			/>
		);
	}

	if (itemType === "WEMSW" || itemType === "WEMSW_CORE") {
		return (
			<WriteEmail
				stem={stem || stimulusText}
				instruction={instruction}
				value={value}
				onChangeText={onChangeText}
				disabled={disabled}
				wordLimit={wordLimit}
				timeLimit={timeLimit}
				initialElapsedSeconds={initialElapsedSeconds}
				onResponseComplete={onResponseComplete}
			/>
		);
	}

	if (itemType === "WFDL" || itemType === "WFDL_CORE") {
		return (
			<WriteFromDictation
				audioUri={audioUri}
				instruction={instruction}
				value={value}
				onChangeText={onChangeText}
				disabled={disabled}
				prepTime={prepTime}
				timeLimit={timeLimit}
				initialElapsedSeconds={initialElapsedSeconds}
				allowAudioReplay={allowAudioReplay}
				onResponseComplete={onResponseComplete}
				onAudioEnded={onAudioEnded}
			/>
		);
	}

	return null;
});

type Props = {
	item: BaseItemSchema;
	isSplitLayout: boolean;
	isSummativeTest?: boolean;
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
const WritingContainer = ({ item, isSplitLayout, isSummativeTest }: Props) => {
	const { content, response } = item;
	const itemId = item.id!;
	const itemType = item.metadata?.type;

	const { isAnswered } = useItemAnsweredState(item);

	const markResponseChangedIfGated = useMarkResponseChangedIfGated();
	const userResponse = useAIFeedbackStore((s) => s.userResponse);
	const setUserResponse = useAIFeedbackStore((s) => s.setUserResponse);
	const markResponseComplete = useResponseControlsStore(
		(s) => s.markResponseComplete,
	);
	const markAudioComplete = useResponseControlsStore(
		(s) => s.markAudioComplete,
	);

	const storedResponse = userResponse?.[itemId];
	const value =
		(typeof storedResponse === "string" ? storedResponse : undefined) ??
		(item.answer?.aiResponse?.answer || undefined) ??
		getSubmittedRawInputValue(item.answer, "text") ??
		item.answer?.userResponseText;

	const onChangeText = useCallback(
		(text: string) => {
			setUserResponse(itemId, text);
			markResponseChangedIfGated(itemId, isSummativeTest, itemType);
		},
		[
			itemId,
			setUserResponse,
			markResponseChangedIfGated,
			isSummativeTest,
			itemType,
		],
	);

	const handleResponseComplete = useCallback(
		() => markResponseComplete(itemId),
		[markResponseComplete, itemId],
	);
	const handleAudioEnded = useCallback(
		() => markAudioComplete(itemId),
		[markAudioComplete, itemId],
	);

	const instruction =
		response.type === "EXTENDED_TEXT" || response.type === "SHORT_TEXT_ENTRY"
			? (response.instruction ?? "")
			: "";
	const wordLimit =
		response.type === "EXTENDED_TEXT" ? response.wordLimit : undefined;
	const showTimerConfig = useAppStore((s) => Boolean(s.sessionView?.timer));
	const responseConfig = getSummativeResponseConfig(
		response,
		isSummativeTest || showTimerConfig,
	);
	const timeLimit = isSummativeTest ? responseConfig?.timeLimit : undefined;
	const initialElapsedSeconds = responseConfig?.overrideTestTimerWithItemTimer
		? (item.answer?.timeElapsed ?? 0)
		: 0;

	const audioUri =
		content.stimulus?.type === "AUDIO"
			? (content.stimulus.media?.[0]?.url ?? "")
			: "";
	const prepTime = responseConfig?.prepTime;
	const allowAudioReplay = getAllowAudioReplay(responseConfig, isSummativeTest);
	const stimulusText =
		content.stimulus?.type === "TEXT" ? (content.stimulus.text ?? "") : "";
	const stem = content.stem?.text ?? "";

	return (
		<Box gap={16}>
			<WritingItemInput
				itemType={itemType}
				audioUri={audioUri}
				stimulusText={stimulusText}
				stem={stem}
				instruction={instruction}
				value={value}
				onChangeText={onChangeText}
				disabled={isAnswered}
				wordLimit={wordLimit}
				prepTime={prepTime}
				timeLimit={timeLimit}
				initialElapsedSeconds={initialElapsedSeconds}
				allowAudioReplay={allowAudioReplay}
				onResponseComplete={handleResponseComplete}
				onAudioEnded={handleAudioEnded}
			/>
			<FormativeTimeOverrunContainer />
			<DesktopFeedbackPanel isSplitLayout={isSplitLayout} />
		</Box>
	);
};

export default WritingContainer;
